import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '../api/client';
import { useApiQuery, useCurrency } from '../api/hooks';
import { fmtMoney } from '../lib/format';
import { currencyOf } from '../lib/fx';
import { errorText, Icon, statusOf } from '../ui/kit';
import type { IconName } from '../ui/icons';
import { STATE_PILL, type OrderState } from '../pages/orders/data';
import { fmtUnit, TYPE_LABEL } from '../pages/masterdata/common';
import type { HelpView } from '../lib/help';
import { BRAND } from '../lib/brand';

// The prototype's search palette (openPalette / paletteResults): quick links when empty, then the
// business's invoices, quotes, purchase orders, bills, contacts and items from GET /v1/search.

type Results = Schemas['SearchResults'];
type Entry = { key: string; icon: IconName; title: ReactNode; sub?: ReactNode; to: string; help?: HelpView };
type Group = { title: string; entries: Entry[] };

/** The shortest query the API accepts. */
export const MIN_QUERY = 2;

const GO_TO: [IconName, string, string][] = [
  ['dashboard', '/', 'Dashboard'],
  ['doc', '/invoices', 'Invoices'],
  ['doc', '/quotes', 'Quotes'],
  ['bill', '/bills', 'Bills'],
  ['box', '/purchase-orders', 'Purchase orders'],
  ['people', '/customers', 'Customers'],
  ['truck', '/suppliers', 'Suppliers'],
  ['box', '/items', 'Items'],
  ['bank', '/banking', 'Banking'],
  ['scale', '/reconcile', 'Reconciliations'],
  ['scale', '/journals/new', 'Journal entry'],
  ['chart', '/accounts', 'Chart of accounts'],
  ['chart', '/reports', 'Reports'],
  ['gear', '/settings', 'Settings'],
];

/** Help commands (APP-DESIGN.md 10.1): they open the help panel at the right view. */
export const HELP_COMMANDS: [string, string, HelpView][] = [
  ['Contact support', 'Ask a question or report a problem', { kind: 'form', type: 'howto' }],
  ['Suggest a feature', 'Tell us what to build next', { kind: 'form', type: 'feature' }],
  ['Search help', 'Help articles and guides', { kind: 'home' }],
  [`Ask ${BRAND.name}`, 'Ask the assistant a question', { kind: 'assistant' }],
];

/** The help commands whose words match what was typed; all of them with nothing typed, or with `all`. */
export function helpCommands(typed: string, all = false): Entry[] {
  const t = typed.trim().toLowerCase();
  return HELP_COMMANDS
    .filter(([title, sub]) => all || !t || `${title} ${sub} help support`.toLowerCase().includes(t))
    .map(([title, sub, view]) => ({ key: `help-${title}`, icon: 'help' as IconName, title, sub, to: '', help: view }));
}

function useDebounced(value: string, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

const orderLabel = (o: Schemas['SearchOrder']) => STATE_PILL[(o.is_expired ? 'expired' : o.status) as OrderState]?.[1] ?? o.status ?? '';

/** Search results as the palette's groups, in the prototype's order. Pure, for testing. */
export function groupsOf(r: Results, currency: string): Group[] {
  const fmt = (v: string | undefined, r: { currency?: string | undefined }) => fmtMoney(v, currencyOf(r, currency));
  const docs = (rows: Schemas['SearchDocument'][], icon: IconName, base: string, draft: string): Entry[] =>
    rows.map((d) => ({ key: `${base}${d.id}`, icon, to: `${base}/${d.id}`, title: d.display_number ?? draft, sub: `${d.contact_name ?? ''} · ${fmt(d.total, d)} · ${statusOf(d).label}` }));
  const orders = (rows: Schemas['SearchOrder'][], icon: IconName, base: string, draft: string): Entry[] =>
    rows.map((o) => ({ key: `${base}${o.id}`, icon, to: `${base}/${o.id}`, title: o.display_number ?? draft, sub: `${o.contact_name ?? ''} · ${fmt(o.total, o)} · ${orderLabel(o)}` }));
  const groups: Group[] = [
    { title: 'Invoices', entries: docs(r.invoices, 'doc', '/invoices', 'Draft invoice') },
    { title: 'Quotes', entries: orders(r.quotes, 'doc', '/quotes', 'Draft quote') },
    { title: 'Purchase orders', entries: orders(r.purchase_orders, 'box', '/purchase-orders', 'Draft purchase order') },
    { title: 'Bills', entries: docs(r.bills, 'bill', '/bills', 'Draft bill') },
    {
      title: 'Customers and suppliers',
      entries: r.contacts.map((c) => ({
        key: `c${c.id}`, icon: c.type === 'vendor' ? 'truck' : 'people', to: `/contacts/${c.id}`, title: c.name,
        sub: [TYPE_LABEL[c.type ?? 'customer'] + (c.is_active ? '' : ' · inactive'), c.email, c.city].filter(Boolean).join(' · '),
      })),
    },
    {
      title: 'Items',
      entries: r.items.map((x) => ({
        key: `i${x.id}`, icon: 'box', to: `/items?item=${x.id}`, title: x.name,
        sub: `${fmtUnit(x.unit_price, currency)}${x.unit ? ` / ${x.unit}` : ''}${x.sku ? ` · ${x.sku}` : ''}${x.is_active ? '' : ' · inactive'}`,
      })),
    },
  ];
  return groups.filter((g) => g.entries.length > 0);
}

export function SearchPalette({ onClose, onHelp }: { onClose: () => void; onHelp?: (view: HelpView) => void }) {
  const navigate = useNavigate();
  const currency = useCurrency();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [text, setText] = useState('');
  const [hl, setHl] = useState(0);
  const q = text.trim();
  const debounced = useDebounced(q, 200);
  const searching = debounced.length >= MIN_QUERY;

  const search = useApiQuery(['search', debounced], async () => (await unwrap(api.GET('/v1/search', { params: { query: { q: debounced } } }))).data as Results, {
    enabled: searching,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });

  // Focus the box on open, and give focus back to whatever opened the palette on close.
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    return () => { if (returnTo && document.contains(returnTo)) returnTo.focus(); };
  }, []);

  const groups: Group[] = useMemo(() => {
    if (q.length < MIN_QUERY) {
      const t = q.toLowerCase();
      const links = GO_TO.filter(([, , label]) => !t || label.toLowerCase().includes(t)).map(([icon, to, label]) => ({ key: `go${to}`, icon, to, title: label }));
      const help = onHelp ? helpCommands(q) : [];
      return [...(links.length ? [{ title: 'Go to', entries: links }] : []), ...(help.length ? [{ title: 'Help', entries: help }] : [])];
    }
    const found = search.data && searching ? groupsOf(search.data, currency) : [];
    // Anything typed can also be asked (APP-DESIGN.md 11.1).
    const askEntry: Entry[] = onHelp ? [{ key: 'ask', icon: 'help', title: `Ask ${BRAND.name}: “${q}”`, sub: 'Opens the assistant with this question', to: '', help: { kind: 'assistant', question: q } }] : [];
    // Typing "help", "support" or "feature" also offers the help commands.
    const help = onHelp && /^(help|supp|cont|sugg|feat|bug|prob)/i.test(q) ? helpCommands(q, true) : [];
    return [...found, { title: 'Help', entries: [...askEntry, ...help.filter((h) => h.help?.kind !== 'assistant')] }].filter((g) => g.entries.length > 0);
  }, [q, search.data, searching, currency, onHelp]);
  const entries = groups.flatMap((g) => g.entries);
  const pending = q.length >= MIN_QUERY && (q !== debounced || search.isFetching);

  useEffect(() => { setHl(0); }, [groups]);
  useEffect(() => { listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' }); }, [hl]);

  const choose = (e: Entry | undefined) => {
    if (!e) return;
    closeRef.current();
    if (e.help) onHelp?.(e.help);
    else navigate(e.to);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHl((n) => Math.min(n + 1, entries.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHl((n) => Math.max(n - 1, 0)); }
    else if (e.key === 'Home') { e.preventDefault(); setHl(0); }
    else if (e.key === 'End') { e.preventDefault(); setHl(Math.max(entries.length - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (!pending || q.length < MIN_QUERY) choose(entries[hl]); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeRef.current(); }
    else if (e.key === 'Tab') e.preventDefault(); // focus stays in the dialog; arrows move through results
  };

  let status: ReactNode = null;
  if (q.length > 0 && q.length < MIN_QUERY && entries.length === 0) status = 'Type one more character to search.';
  else if (q.length >= MIN_QUERY && entries.length === 0) {
    if (search.isError && !pending) status = errorText(search.error);
    else if (pending || !search.data) status = 'Searching…';
    else status = <>Nothing matches “{q}”. Try a number such as INV-0003, a name, an email or a SKU.</>;
  }

  const optionId = (n: number) => `${listId}-opt-${n}`;
  let n = -1;
  return createPortal(
    <div className="scrim top" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="palette" role="dialog" aria-modal="true" aria-label="Search">
        <div className="palette-input">
          <Icon name="search" size={18} />
          <input
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search invoices, bills, customers, suppliers and items"
            autoComplete="off"
            spellCheck={false}
            maxLength={100}
            aria-label="Search"
            role="combobox"
            aria-expanded={entries.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={entries.length ? optionId(hl) : undefined}
          />
          <span className="pop-kbd">Esc</span>
        </div>
        <div className="palette-results" id={listId} ref={listRef} role="listbox" aria-label={q.length < MIN_QUERY ? 'Go to' : 'Search results'} aria-busy={pending}>
          {groups.map((g) => (
            <div role="group" aria-label={g.title} key={g.title}>
              <div className="pop-title" aria-hidden="true">{g.title}</div>
              {g.entries.map((e) => {
                n += 1;
                const i = n;
                return (
                  <div key={e.key} id={optionId(i)} className={`pop-item ${i === hl ? 'hl' : ''}`} role="option" aria-selected={i === hl}
                    onMouseMove={() => { if (i !== hl) setHl(i); }} onMouseDown={(ev) => ev.preventDefault()} onClick={() => choose(e)}>
                    <span className="pop-ic"><Icon name={e.icon} /></span>
                    <span style={{ minWidth: 0, flex: 1 }}><span className="pi-title" style={{ display: 'block' }}>{e.title}</span>{e.sub && <span className="pi-sub" style={{ display: 'block' }}>{e.sub}</span>}</span>
                  </div>
                );
              })}
            </div>
          ))}
          {status && <div className="pop-empty">{status}</div>}
        </div>
        <div className="visually-hidden" aria-live="polite">{q.length >= MIN_QUERY && !pending ? (entries.length ? `${entries.length} results` : 'No results') : ''}</div>
      </div>
    </div>,
    document.body,
  );
}
